import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendConsultationEmail, ConsultationData } from '../services/emailService';
import './ConsultationForm.css';

interface ConsultationFormProps {
  productName: string;
}

const ConsultationForm: React.FC<ConsultationFormProps> = ({ productName }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    business: '',
    fax: '',
    captcha: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [captchaCode, setCaptchaCode] = useState('');

  // 生成验证码
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 4; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(result);
  };

  // 初始化验证码
  React.useEffect(() => {
    generateCaptcha();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 验证必填字段
    if (!formData.name || !formData.phone || !formData.email || !formData.business || !formData.captcha) {
      alert(t('consultationForm.requiredFields'));
      return;
    }

    // 验证验证码
    if (formData.captcha.toUpperCase() !== captchaCode) {
      alert(t('consultationForm.captchaError'));
      generateCaptcha();
      setFormData(prev => ({ ...prev, captcha: '' }));
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // 直接发送邮件
      const emailData: ConsultationData = {
        productName,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        business: formData.business,
        fax: formData.fax
      };

      const result = await sendConsultationEmail(emailData);

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          business: '',
          fax: '',
          captcha: ''
        });
        generateCaptcha();
      } else {
        throw new Error(result.message || '邮件发送失败');
      }
    } catch (error) {
      console.error('提交失败:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="consultation-form-container">
      <h3 className="consultation-form-title">{t('consultationForm.title')}</h3>
      
      {submitStatus === 'success' && (
        <div className="success-message">
          {t('consultationForm.submitSuccess')}
        </div>
      )}
      
      {submitStatus === 'error' && (
        <div className="error-message">
          {t('consultationForm.submitError')}
        </div>
      )}

      <form onSubmit={handleSubmit} className="consultation-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">{t('consultationForm.name')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">{t('consultationForm.phone')}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">{t('consultationForm.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="fax">{t('consultationForm.fax')}</label>
            <input
              type="text"
              id="fax"
              name="fax"
              value={formData.fax}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-group full-width">
          <label htmlFor="business">{t('consultationForm.business')}</label>
          <input
            type="text"
            id="business"
            name="business"
            value={formData.business}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group captcha-group">
            <label htmlFor="captcha">{t('consultationForm.captcha')}</label>
            <input
              type="text"
              id="captcha"
              name="captcha"
              value={formData.captcha}
              onChange={handleInputChange}
              required
              placeholder={t('consultationForm.captchaPlaceholder')}
            />
            <div className="captcha-display" onClick={generateCaptcha}>
              {captchaCode}
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('consultationForm.submitting') : t('consultationForm.submit')}
        </button>
      </form>
    </div>
  );
};

export default ConsultationForm;
